import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { Toast } from 'react-hot-toast';
import toast from 'react-hot-toast';

import { NotificationBanner } from './NotificationBanner';

export interface PromiseNotificationBannerProps {
  type: 'promise';
  promise: Promise<unknown>;
  successMessage: ReactNode;
  errorMessage: ReactNode;
  loadingMessage?: ReactNode;
  /** Reject values matching this dismiss the toast silently (no error shown). */
  suppressOn?: (error: unknown) => boolean;
  toast: Toast;
}

export const PromiseNotificationBanner = (
  props: PromiseNotificationBannerProps
) => {
  const { promise, suppressOn } = props;
  const toastId = props.toast.id;
  const [state, setState] = useState<'error' | 'loading' | 'success'>(
    'loading'
  );

  useEffect(() => {
    setState('loading');
    promise
      .then(() => {
        setState('success');
      })
      .catch((error: unknown) => {
        // A "no-op" outcome (e.g. user-cancelled change-request confirm) should
        // produce no toast at all — dismiss silently rather than show an error.
        if (suppressOn?.(error)) {
          toast.dismiss(toastId);
        } else {
          setState('error');
        }
      });
  }, [promise, suppressOn, toastId]);

  const content = useMemo(() => {
    switch (state) {
      case 'loading':
        return props.loadingMessage ?? 'Saving changes...';
      case 'success':
        return props.successMessage;
      case 'error':
        return props.errorMessage;
    }
  }, [state, props.errorMessage, props.successMessage, props.loadingMessage]);

  return (
    <NotificationBanner
      toast={props.toast}
      type={state}
      content={content}
      durationMs={state === 'success' ? 3500 : Infinity}
      dismissable={state === 'error'}
    />
  );
};
